import { useState } from 'react';
import Layout from '@/components/feature/Layout';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOHead from '@/components/base/SEOHead';
import { teamMembers, milestones } from '@/mocks/siteData';

const teamAvatars: Record<string, string> = {
  'team-01': 'https://readdy.ai/api/search-image?query=Professional%20Asian%20male%20scholar%20portrait%20in%20warm%20library%20setting%2C%20clean%20portrait%20photography%20with%20soft%20natural%20lighting%2C%20intellectual%20and%20approachable%20expression%2C%20neutral%20warm%20background&width=400&height=400&seq=team-avatar-01&orientation=squarish',
  'team-02': 'https://readdy.ai/api/search-image?query=Professional%20Asian%20female%20researcher%20portrait%20in%20modern%20office%20setting%2C%20clean%20portrait%20photography%20with%20soft%20diffused%20lighting%2C%20confident%20and%20warm%20expression%2C%20neutral%20warm%20background&width=400&height=400&seq=team-avatar-02&orientation=squarish',
  'team-03': 'https://readdy.ai/api/search-image?query=Professional%20Asian%20male%20academic%20portrait%20in%20university%20setting%2C%20clean%20portrait%20photography%20with%20soft%20natural%20lighting%2C%20thoughtful%20expression%2C%20neutral%20warm%20background&width=400&height=400&seq=team-avatar-03&orientation=squarish',
};

function ContactForm() {
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formPayload = new FormData(form);
    const honeypot = (formPayload.get('company_alt') as string || '').trim();
    if (honeypot) {
      setStatus('success');
      setFormData({ name: '', email: '', message: '' });
      return;
    }
    if (formData.message.length > 500) {
      setStatus('error');
      setErrorMsg('留言内容不能超过500个字符');
      return;
    }
    setStatus('loading');
    setErrorMsg('');
    try {
      const body = new URLSearchParams({
        name: formPayload.get('name') as string,
        email: formPayload.get('email') as string,
        message: formPayload.get('message') as string,
      }).toString();
      const res = await fetch('https://readdy.ai/api/form/d9srl8l8o1vn02ekul8g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body,
      });
      const text = await res.text();
      let parsed: Record<string, unknown> = {};
      try { parsed = JSON.parse(text); } catch { /* ignore */ }
      if (res.ok && (parsed as { code?: string }).code === 'OK') {
        setStatus('success');
        setFormData({ name: '', email: '', message: '' });
      } else {
        const msg = (parsed as { meta?: { message?: string } })?.meta?.message
          || (parsed as { message?: string })?.message
          || text
          || '提交失败，请稍后重试';
        setStatus('error');
        setErrorMsg(String(msg));
      }
    } catch {
      setStatus('error');
      setErrorMsg('网络异常，请检查连接后重试');
    }
  };

  return (
    <div className="bg-background-50 border border-background-200/70 rounded-lg p-6 md:p-8">
      <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-6">发送消息</h3>

      {status === 'success' ? (
        <div className="text-center py-8">
          <div className="w-14 h-14 rounded-full bg-primary-100 flex items-center justify-center mx-auto mb-4">
            <i className="ri-check-line text-2xl text-primary-600"></i>
          </div>
          <p className="text-foreground-800 font-medium text-lg mb-1">发送成功！</p>
          <p className="text-foreground-500 text-sm">感谢您的留言，我们会在1-2个工作日内回复。</p>
        </div>
      ) : (
        <form onSubmit={handleSubmit} data-readdy-form>
          <div className="space-y-4">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                姓名 <span className="text-primary-500">*</span>
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                className="w-full px-4 py-2.5 bg-background-100 border border-background-200 rounded-lg text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                placeholder="请输入您的姓名"
              />
            </div>
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                电子邮箱 <span className="text-primary-500">*</span>
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                className="w-full px-4 py-2.5 bg-background-100 border border-background-200 rounded-lg text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                placeholder="请输入您的电子邮箱"
              />
            </div>
            <div>
              <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                留言内容 <span className="text-primary-500">*</span>
                <span className="text-foreground-400 font-normal ml-2">({formData.message.length}/500)</span>
              </label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                required
                maxLength={500}
                rows={5}
                className="w-full px-4 py-2.5 bg-background-100 border border-background-200 rounded-lg text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                placeholder="请输入您的留言内容（不超过500字）"
              />
            </div>
            <input
              type="text"
              name="company_alt"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              readOnly
            />
            <button
              type="submit"
              disabled={status === 'loading'}
              className="w-full py-3 bg-primary-500 text-background-50 rounded-lg font-medium text-sm hover:bg-primary-600 transition-all duration-300 disabled:opacity-60 whitespace-nowrap"
            >
              {status === 'loading' ? '发送中...' : '发送消息'}
            </button>
          </div>
          {status === 'error' && (
            <p className="text-red-500 text-xs mt-3">{errorMsg}</p>
          )}
        </form>
      )}
    </div>
  );
}

export default function AboutPage() {
  return (
    <Layout>
      <SEOHead
        title="关于我们 · 里番文化研究所"
        description="里番文化研究所成立于2018年，是中文互联网首个专注于日本里番动画文化学术研究的独立平台，为成年用户提供客观、理性、专业的里番文化资讯与研究内容。"
        keywords="里番文化研究所,关于我们,里番研究,成人动画研究,联系我们"
        canonicalUrl="https://example.com/about"
      />

      <section className="relative w-full h-[300px] md:h-[380px] overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=Modern%20academic%20research%20institute%20interior%20with%20bookshelves%20and%20warm%20reading%20area%2C%20Japanese%20cultural%20elements%20subtly%20integrated%2C%20professional%20and%20elegant%20atmosphere%2C%20soft%20natural%20lighting%2C%20architectural%20photography&width=1600&height=700&seq=about-hero-01&orientation=landscape"
          alt="里番文化研究所"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/40 to-black/65"></div>
        <div className="absolute inset-0 flex items-center justify-center px-4">
          <div className="text-center max-w-2xl">
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-background-50 mb-4">
              关于我们
            </h1>
            <p className="text-background-100/80 text-sm md:text-base max-w-xl mx-auto">
              致力于日本里番动画文化的学术研究与知识传播，为成年用户提供客观、理性、全面的里番文化资讯与深度分析内容。
            </p>
          </div>
        </div>
      </section>

      <section className="py-12 md:py-16 px-4 md:px-6 bg-background-50">
        <div className="max-w-5xl mx-auto">
          <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '关于我们' }]} />

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-10">
            <div className="lg:col-span-3">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-5">
                品牌介绍
              </h2>
              <div className="space-y-4 text-foreground-600 leading-relaxed text-sm">
                <p>
                  <strong className="text-foreground-900">里番文化研究所</strong>成立于2018年，是中文互联网领域中首个专注于日本里番动画文化学术研究的独立平台。我们的使命是：以客观、理性、专业的学术态度，为成年用户构建关于日本里番动画的系统性知识框架。
                </p>
                <p>
                  在数字媒体高速发展、日本动画文化全球传播的当下，里番作为日本动画产业中的一个特殊分支，长期以来缺乏系统性的学术关注和理性的公共讨论。我们的存在，正是为了填补这一认知空白。
                </p>
                <p>
                  我们坚持以下核心原则：<strong className="text-foreground-900">学术性</strong>——以严谨的研究方法对待每一个选题；<strong className="text-foreground-900">客观性</strong>——不带偏见地呈现事实与多元观点；<strong className="text-foreground-900">专业性</strong>——由具备相关领域背景的研究者主笔；<strong className="text-foreground-900">合规性</strong>——严格遵守内容发布的法律法规与伦理边界。
                </p>
                <p>
                  截至目前，研究所已累计发表深度研究文章超过500篇，建立了中文领域最完整的日本里番动画数据库，收录作品信息超过2000条。我们的内容涵盖历史起源、类型分类、文化背景、产业分析和观看须知五大研究领域。
                </p>
              </div>

              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mt-12 mb-5">
                行业声誉
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
                <div className="bg-background-100 border border-background-200/70 rounded-lg p-5 text-center">
                  <div className="text-3xl font-heading font-bold text-primary-600 mb-2">500+</div>
                  <p className="text-xs text-foreground-500">深度研究文章</p>
                </div>
                <div className="bg-background-100 border border-background-200/70 rounded-lg p-5 text-center">
                  <div className="text-3xl font-heading font-bold text-accent-600 mb-2">2000+</div>
                  <p className="text-xs text-foreground-500">收录作品数据</p>
                </div>
                <div className="bg-background-100 border border-background-200/70 rounded-lg p-5 text-center">
                  <div className="text-3xl font-heading font-bold text-secondary-600 mb-2">50万+</div>
                  <p className="text-xs text-foreground-500">月均访问量</p>
                </div>
              </div>

              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mt-12 mb-5">
                发展历程
              </h2>
              <div className="relative pl-6 border-l-2 border-background-200 space-y-6">
                {milestones.map((m, i) => (
                  <div key={i} className="relative">
                    <div className="absolute -left-[29px] top-0.5 w-4 h-4 rounded-full bg-primary-500 border-2 border-background-50"></div>
                    <span className="text-xs font-semibold text-primary-600 bg-primary-100 px-2 py-0.5 rounded-full inline-block mb-1.5">
                      {m.year}
                    </span>
                    <p className="text-sm text-foreground-600">{m.event}</p>
                  </div>
                ))}
              </div>

              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mt-12 mb-5">
                研究团队
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {teamMembers.map((member) => (
                  <div key={member.name} className="bg-background-100 border border-background-200/70 rounded-lg p-5 text-center">
                    <img
                      src={teamAvatars[member.avatarSeq]}
                      alt={member.name}
                      className="w-20 h-20 rounded-full object-cover mx-auto mb-3"
                    />
                    <h4 className="font-heading text-sm font-semibold text-foreground-900">{member.name}</h4>
                    <p className="text-xs text-primary-600 mb-2">{member.role}</p>
                    <p className="text-xs text-foreground-500 leading-relaxed">{member.bio}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="lg:col-span-2">
              <div className="sticky top-24 space-y-6">
                <ContactForm />

                <div className="bg-background-100 border border-background-200/70 rounded-lg p-6">
                  <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-4">联系方式</h3>
                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <div className="w-9 h-9 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                        <i className="ri-mail-line text-primary-600"></i>
                      </div>
                      <div>
                        <p className="text-xs text-foreground-500 mb-0.5">电子邮箱</p>
                        <p className="text-sm text-foreground-800">contact@lifan-research.org</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-9 h-9 rounded-lg bg-accent-100 flex items-center justify-center flex-shrink-0">
                        <i className="ri-map-pin-line text-accent-600"></i>
                      </div>
                      <div>
                        <p className="text-xs text-foreground-500 mb-0.5">办公地址</p>
                        <p className="text-sm text-foreground-800">东京都千代田区神田神保町2-3-15 文化研究大楼4F</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-9 h-9 rounded-lg bg-secondary-100 flex items-center justify-center flex-shrink-0">
                        <i className="ri-time-line text-secondary-600"></i>
                      </div>
                      <div>
                        <p className="text-xs text-foreground-500 mb-0.5">工作时间</p>
                        <p className="text-sm text-foreground-800">周一至周五 10:00–18:00（日本标准时间）</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}